import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import AdvertiserBrandCard from './AdvertiserBrandCard';
import CreateAdvertiserModal from './CreateAdvertiserModal';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import ConfirmModal from '../form-components/ConfirmModal';
import {
  getAdvertisers, getAdvertisersForSpace, createAdvertiser, updateAdvertiser, deleteAdvertiser,
} from '../../api/advertiserApi';
import { submitApproval } from '../../api/approvalApi';
import { useSpaceId } from '../../hooks/useSpaceId';
import { useToast } from '../../context/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import StatusTabs from '../approval/StatusTabs';
import { buildVersionedList, filterVersionedList, countVersionedTabs } from '../../utils/versionedContent';
import { signalRService } from '../../services/signalRService';
import { SIGNALR_EVENTS } from '../../constants/signalREvents';
import { CONTENT_TYPES } from '../../constants/contentTypes';

const AdvertiserBrandList = ({ onSelectBrand, overrideSpaceId }) => {
  const spaceId = useSpaceId();
  const effectiveSpaceId = overrideSpaceId ?? spaceId;
  const { toast } = useToast();
  const { canFeature } = usePermissions();
  const canEdit = canFeature('/advertisers', 'advertiser-edit');
  const searchPlaceholder  = useLocalText('Search advertisers…');
  const [advertisers, setAdvertisers] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tabValue, setTabValue] = useState('live');
  const [highlight, setHighlight] = useState({ tab: null, token: 0 });
  const flashTab = useCallback((tab) => setHighlight({ tab, token: Date.now() }), []);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalReadOnly, setModalReadOnly] = useState(false);
  const [proposeEditMode, setProposeEditMode] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [proposeDeleteTarget, setProposeDeleteTarget] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(() =>
    (overrideSpaceId ? getAdvertisersForSpace(overrideSpaceId) : getAdvertisers())
      .then(setAdvertisers)
      .catch(() => {}),
  [overrideSpaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!effectiveSpaceId) return;
    load();
  }, [effectiveSpaceId, load]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  useEffect(() => {
    const handleDecision = (payload) => {
      if (payload?.contentType === CONTENT_TYPES.ADVERTISER) load();
    };
    signalRService.on(SIGNALR_EVENTS.APPROVAL_DECISION, handleDecision);
    return () => signalRService.off(SIGNALR_EVENTS.APPROVAL_DECISION, handleDecision);
  }, [load]);

  useEffect(() => {
    const handleWithdrawn = (payload) => {
      if (payload?.contentType === CONTENT_TYPES.ADVERTISER) load();
    };
    signalRService.on(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, handleWithdrawn);
    return () => signalRService.off(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, handleWithdrawn);
  }, [load]);

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearch(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebouncedSearch(v), 250);
  };

  const versioned = useMemo(() => buildVersionedList(advertisers), [advertisers]);
  const counts    = useMemo(() => countVersionedTabs(advertisers),  [advertisers]);

  const filtered = useMemo(() => {
    let result = filterVersionedList(versioned, tabValue);
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return result;
    return result.filter(
      (a) =>
        a.localizedName?.values?.some((v) => v.value.toLowerCase().includes(q)) ||
        a.industry?.toLowerCase().includes(q)
    );
  }, [versioned, debouncedSearch, tabValue]);

  const handleSave = async (data) => {
    try {
      if (proposeEditMode) {
        const contentName = data.localizedName?.values?.[0]?.value || `Advertiser ${editTarget.id}`;
        await submitApproval({
          contentType: CONTENT_TYPES.ADVERTISER,
          contentId: editTarget.id,
          contentName: contentName,
          actionType: 'edit',
          proposedData: JSON.stringify(data),
          proposedSnapshot: JSON.stringify(data),
        });
        toast.success('Edit submitted for approval!');
        setProposeEditMode(false);
        setEditTarget(null);
        setModalOpen(false);
        await load();
        flashTab('pending');
      } else {
        if (editTarget) {
          await updateAdvertiser(editTarget.id, data);
        } else {
          await createAdvertiser(data);
          toast.success('Advertiser created and moved to Draft.');
        }
        setEditTarget(null);
        await load();
        flashTab('draft');
      }
    } catch (err) {
      throw err;
    }
  };

  const handleEdit = (advertiser, proposeMode = false) => {
    setEditTarget(advertiser);
    setModalReadOnly(false);
    setProposeEditMode(proposeMode);
    setModalOpen(true);
  };

  const handleEditRejected = (proposedSnap, liveId) => {
    setEditTarget({ ...proposedSnap, id: liveId });
    setProposeEditMode(true);
    setModalReadOnly(false);
    setModalOpen(true);
  };

  const handleView = (advertiser) => {
    setEditTarget(advertiser);
    setModalReadOnly(true);
    setProposeEditMode(false);
    setModalOpen(true);
  };

  const handleDelete = (advertiser, proposeMode = false) => {
    if (proposeMode) setProposeDeleteTarget(advertiser);
    else setDeleteTarget(advertiser);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAdvertiser(deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch {
      setDeleteTarget(null);
    }
  };

  const confirmProposeDelete = async () => {
    if (!proposeDeleteTarget) return;
    try {
      const contentName = proposeDeleteTarget.localizedName?.values?.[0]?.value || `Advertiser ${proposeDeleteTarget.id}`;
      await submitApproval({
        contentType: CONTENT_TYPES.ADVERTISER,
        contentId: proposeDeleteTarget.id,
        contentName: contentName,
        actionType: 'delete',
      });
      toast.success('Deletion request submitted for approval.');
      setProposeDeleteTarget(null);
      await load();
      flashTab('pending');
    } catch {
      setProposeDeleteTarget(null);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditTarget(null);
    setModalReadOnly(false);
    setProposeEditMode(false);
  };

  return (
    <Box>
      <StatusTabs items={advertisers} counts={counts} value={tabValue} onChange={setTabValue} sx={{ mb: 1 }} highlightTab={highlight.tab} highlightToken={highlight.token} />

      {/* Toolbar */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#85a5c6' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 260 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: 'white',
                '& fieldset': { borderColor: '#dde6f0' },
                '&:hover fieldset': { borderColor: '#5383b3' },
                '&.Mui-focused fieldset': { borderColor: '#0a326d' },
              },
            }}
          />
          <Typography variant="caption" sx={{ color: '#85a5c6', fontWeight: 600 }}>
            {filtered.length} {filtered.length === 1 ? <LocalText>brand</LocalText> : <LocalText>brands</LocalText>}
          </Typography>
        </Box>

        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={(e) => { e.currentTarget.blur(); setModalOpen(true); }}
            sx={{
              bgcolor: '#0a326d',
              color: 'white',
              borderRadius: '12px',
              px: 2.5,
              py: 1,
              fontWeight: 600,
              fontSize: 13,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(10,50,109,0.25)',
              '&:hover': { bgcolor: '#0d3d85', boxShadow: '0 6px 16px rgba(10,50,109,0.35)' },
            }}
          >
            <LocalText>New Advertiser</LocalText>
          </Button>
        )}
      </Box>

      {/* Empty state */}
      {filtered.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <BusinessIcon sx={{ fontSize: 56, color: '#c8d8ea', mb: 1.5 }} />
          <Typography variant="body1" sx={{ color: '#85a5c6', fontWeight: 500 }}>
            {debouncedSearch ? <LocalText>No advertisers match your search.</LocalText> : <LocalText>No advertisers yet. Add one!</LocalText>}
          </Typography>
        </Box>
      )}

      {/* Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
          gap: 2.5,
        }}
      >
        {filtered.map((a) => (
          <AdvertiserBrandCard
            key={a._viewType === 'snapshot' ? `snap-${a._liveId}` : a.id}
            advertiser={a}
            onSelect={onSelectBrand}
            onEdit={handleEdit}
            onView={handleView}
            onDelete={handleDelete}
            onSuccess={(targetTab) => { load(); if (targetTab) flashTab(targetTab); }}
            showActions={canEdit}
            onEditRejected={canEdit ? handleEditRejected : undefined}
          />
        ))}
      </Box>

      <CreateAdvertiserModal
        open={modalOpen}
        onClose={handleModalClose}
        onSave={handleSave}
        initial={editTarget}
        readOnly={modalReadOnly}
      />

      <ConfirmModal
        open={Boolean(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        severity="danger"
        title="Delete Advertiser"
        message={`Delete "${deleteTarget?.localizedName?.values?.[0]?.value ?? 'this advertiser'}"?`}
        subtitle="This will also delete all campaigns and ads under this advertiser."
        confirmLabel="Delete"
      />

      <ConfirmModal
        open={Boolean(proposeDeleteTarget)}
        onCancel={() => setProposeDeleteTarget(null)}
        onConfirm={confirmProposeDelete}
        severity="warning"
        title="Propose Deletion"
        message={`Submit a delete request for "${proposeDeleteTarget?.localizedName?.values?.[0]?.value ?? 'this advertiser'}"? The content remains active until an approver approves.`}
        confirmLabel="Submit for Approval"
      />
    </Box>
  );
};

export default AdvertiserBrandList;
